/**
 * Phase 10 - Public post-event survey page.
 * URL: /events/:slug/survey?t={signed_token}
 * No auth required. Uses the token to fetch questions + submit responses.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Star, CheckCircle2 } from 'lucide-react';
import api from '@/utils/api';

const StarInput = ({ value, onChange, scale = 5, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {Array.from({ length: scale }, (_, i) => i + 1).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-1 group"
        aria-label={`${n} of ${scale}`}
      >
        <Star
          className={`h-7 w-7 transition-all ${n <= (value || 0) ? 'text-secondary-500 fill-secondary-500' : 'text-neutral-300 group-hover:text-secondary-400'}`}
        />
      </button>
    ))}
  </div>
);

const SurveyPage = () => {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [event, setEvent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [myTestimonial, setMyTestimonial] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing survey link.'); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/survey/${token}`);
        setQuestions(data.questions || []);
        setEvent(data.event || null);
        if (data.prior_response) setAlreadySubmitted(true);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Could not load survey.');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const setVal = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/survey/${token}`, { responses: answers });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Submit failed.');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-500">Loading survey...</div>;
  }
  if (error && !submitted) {
    return (
      <div className="min-h-screen bg-neutral-50 py-16 px-4">
        <Card className="max-w-md mx-auto"><CardContent className="p-8 text-center space-y-3">
          <div className="font-display text-2xl font-bold text-primary-900">Survey unavailable</div>
          <p className="text-neutral-600">{error}</p>
          <Button asChild variant="outline"><Link to="/events">Back to events</Link></Button>
        </CardContent></Card>
      </div>
    );
  }
  if (submitted || alreadySubmitted) {
    // Bundle 83 - resolve the participant's testimonial (if any) so
    // we can offer a share card link on the thank-you screen. Runs
    // once when the screen renders in `submitted` state.
    if (submitted && myTestimonial === null) {
      (async () => {
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/testimonials/by-survey-token/${token}`,
          );
          setMyTestimonial(data || { none: true });
        } catch (_) {
          setMyTestimonial({ none: true });
        }
      })();
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50/30 via-white to-secondary-50/40 py-16 px-4">
        <Card className="max-w-md mx-auto" data-testid="survey-thankyou">
          <CardContent className="p-8 space-y-4 text-center">
            <CheckCircle2 className="h-14 w-14 text-success-600 mx-auto" />
            <div className="font-display text-2xl md:text-3xl font-bold text-primary-900">Thanks for sharing.</div>
            <p className="text-neutral-600">Your feedback shapes the next Dostination event.</p>
            {myTestimonial?.id ? (
              <div className="border-t border-neutral-200 pt-4 space-y-2"
                    data-testid="survey-thankyou-share-card">
                <div className="text-sm font-medium text-primary-900">Loved your response? Share it as a story card.</div>
                <div className="text-xs text-neutral-500">Instagram-story-sized, branded, and stripped of any personal data you did not opt in to.</div>
                <Button asChild className="bg-secondary-600 hover:bg-secondary-700 text-white">
                  <Link to={myTestimonial.share_page_url} target="_blank"
                        data-testid="survey-thankyou-share-link">Open share page</Link>
                </Button>
              </div>
            ) : myTestimonial?.pending ? (
              <div className="border-t border-neutral-200 pt-4 space-y-1"
                    data-testid="survey-thankyou-share-pending">
                <div className="text-sm text-primary-900">Thanks for allowing a public quote.</div>
                <div className="text-xs text-neutral-500">
                  Once our team approves it, you will be able to share it as a branded card here.
                </div>
              </div>
            ) : null}
            <Button asChild className="bg-primary-800 hover:bg-primary-900 text-white">
              <Link to="/events">Explore upcoming events</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50/30 via-white to-secondary-50/40 py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center space-y-1">
          <div className="text-xs uppercase tracking-widest text-secondary-700">Post-event survey</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-primary-900">
            How was {event?.title || 'the event'}?
          </h1>
          <p className="text-neutral-600 text-sm">Two minutes of feedback. Your responses stay private.</p>
        </div>

        <Card className="border shadow-soft"><CardContent className="p-6 md:p-8 space-y-6" data-testid="survey-form">
          {questions.map((q) => (
            <div key={q.id} className="space-y-2" data-testid={`survey-q-${q.id}`}>
              <Label className="text-sm font-semibold text-primary-900">
                {q.label}{q.required && <span className="text-accent-500"> *</span>}
              </Label>
              {q.type === 'rating' && (
                <StarInput value={answers[q.id]} onChange={(v) => setVal(q.id, v)} scale={q.scale || 5} testid={`survey-rating-${q.id}`} />
              )}
              {q.type === 'choice' && (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => (
                    <button key={opt} type="button" onClick={() => setVal(q.id, opt)}
                            className={`px-4 py-1.5 rounded-full border-2 text-sm capitalize transition-all ${answers[q.id] === opt ? 'border-secondary-500 bg-secondary-50 text-primary-900' : 'border-neutral-200 text-neutral-600 hover:border-secondary-300'}`}
                            data-testid={`survey-choice-${q.id}-${opt}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {q.type === 'multiselect' && (
                <div className="flex flex-wrap gap-2">
                  {q.options.map((opt) => {
                    const arr = answers[q.id] || [];
                    const on = arr.includes(opt);
                    return (
                      <button key={opt} type="button"
                              onClick={() => setVal(q.id, on ? arr.filter((v) => v !== opt) : [...arr, opt])}
                              className={`px-3 py-1 rounded-full border text-xs capitalize ${on ? 'border-secondary-500 bg-secondary-50 text-primary-900' : 'border-neutral-200 text-neutral-600 hover:border-secondary-300'}`}
                              data-testid={`survey-multi-${q.id}-${opt}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              )}
              {q.type === 'textarea' && (
                <Textarea
                  rows={3}
                  maxLength={q.maxLength || 2000}
                  value={answers[q.id] || ''}
                  onChange={(e) => setVal(q.id, e.target.value)}
                  data-testid={`survey-textarea-${q.id}`}
                />
              )}
            </div>
          ))}
        </CardContent></Card>

        {error && <div className="text-sm text-accent-700">{error}</div>}
        <Button onClick={submit} disabled={busy}
                className="w-full bg-primary-800 hover:bg-primary-900 text-white font-semibold py-6"
                data-testid="survey-submit-btn">
          {busy ? 'Submitting...' : 'Submit feedback'}
        </Button>
      </div>
    </div>
  );
};

export default SurveyPage;
